import {
  P2GBox,
  P2GButton,
  P2GCheckBoxLegacy,
  P2GChip,
  P2GContainer,
  P2GGrid,
  P2GLink,
  P2GText,
  P2GTextField
} from '@parcel2go/core-ui';
import { SidePanelContent } from '~/components/sidepanel-section';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { AppSurface } from '~/components/surface/app-surface';
import { useMemo, useState } from 'react';
import { AppCard } from '~/components/card/app-card';
import { RuleNames } from '~/apps/smartsend/components/smartsend/rules/RuleGenerator';
import { CircularPercentageProgress } from './circular-percentage-progress';
import { OnboardingTitle } from './onboarding-title';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import OnboardingScreenSpacing from '../spacing-consts';
import { SumNumber } from '~/lib/utils/array';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';

interface props {
  onDone: (rules: string[]) => void;
  onSkip: () => void;
  isXs: boolean;
  numberOfParcels: number;
}

const possibleRules = [
  {
    label: 'Choose a default <strong>service</strong>',
    translationkey: 'SETUP_RULES_DEFAULT_SERVICE_OPTION',
    name: RuleNames.DomesticService,
    percentageSaved: 40
  },
  {
    label: 'Choose default <strong>parcel dimensions</strong>',
    translationkey: 'SETUP_RULES_DEFAULT_DIMENSIONS_OPTION',
    name: RuleNames.Dimensions,
    percentageSaved: 25
  },
  {
    label: 'Choose a default <strong>parcel weight</strong>',
    translationkey: 'SETUP_RULES_DEFAULT_WEIGHT_OPTION',
    name: RuleNames.Weight,
    percentageSaved: 20
  }
];

const Messaging = ({ selectedRules, hideHeader }: { selectedRules: string[]; hideHeader?: boolean }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <>
      {!hideHeader && (
        <P2GText variant="subtitle2" component="div">
          {t('SETUP_RULES_TIME_SAVING_MESSAGE', {
            defaultValue: 'These defaults will fill in any missing information.'
          })}
        </P2GText>
      )}
      {selectedRules.length === 0 ? (
        <P2GText>
          {t('SETUP_RULES_TIME_SAVING_NO_RULES_MESSAGE', {
            defaultValue: 'Selecting some rules will save you time when processing your parcels.'
          })}
        </P2GText>
      ) : selectedRules.length === 1 ? (
        <P2GText>
          {t('SETUP_RULES_TIME_SAVING_ONE_RULE_MESSAGE', {
            defaultValue: "We'll create a {{selectedRule}} rule to save hours of manual processing.",
            selectedRule: selectedRules[0].toLocaleLowerCase()
          })}
        </P2GText>
      ) : (
        <P2GText>
          {t('SETUP_RULES_TIME_SAVING_MULTIPLE_RULES_MESSAGE', {
            defaultValue: "We'll create {{selectedRulesCount}} defaults to save hours of manual processing.",
            selectedRulesCount: selectedRules.length
          })}
        </P2GText>
      )}
    </>
  );
};
const BasedOnSending = ({
  parcelsPerWeek,
  setParcelsPerWeek
}: {
  parcelsPerWeek: number;
  setParcelsPerWeek: (value: number) => void;
}) => {
  const [edit, setEdit] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <>
      <P2GText variant="caption" textAlign="center">
        {t('SETUP_RULES_TIME_SAVING_FOOTNOTE', {
          defaultValue:
            'Based on sending {{parcelsPerWeek}} parcels per week with 2 minutes prep time per parcel without rules.',
          parcelsPerWeek: parcelsPerWeek
        })}
        {!edit && <P2GLink onClick={() => setEdit(true)}>{t('EDIT_BUTTON', { defaultValue: 'Edit' })}</P2GLink>}
      </P2GText>
      {edit && (
        <P2GBox mt={2}>
          <P2GTextField
            fullWidth
            value={parcelsPerWeek}
            onChange={(e) => setParcelsPerWeek(Number(e.target.value))}
            label={t('SETUP_RULES_TIME_SAVING_PARCELES_PER_WEEK_LABEL', {
              defaultValue: 'Parcels sent per week'
            })}
          />
        </P2GBox>
      )}
    </>
  );
};

const TimeSaved = ({
  savedAnnualParcelProcessingTimeInMinutes,
  timeSavedInHours
}: {
  savedAnnualParcelProcessingTimeInMinutes: number;
  timeSavedInHours: number;
}) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <>
      {savedAnnualParcelProcessingTimeInMinutes === 0 ? (
        <P2GText>
          {t('SETUP_RULES_TIME_SAVING_NO_TIME_SAVED_MESSAGE', {
            defaultValue: 'Select some rules to see how much time you could save.'
          })}
        </P2GText>
      ) : savedAnnualParcelProcessingTimeInMinutes < 60 ? (
        <P2GText>
          {t('SETUP_RULES_TIME_SAVING_MINUTES_MESSAGE', {
            defaultValue: 'Over the next year you could save up to {{minutes}} minutes*',
            minutes: savedAnnualParcelProcessingTimeInMinutes
          })}
        </P2GText>
      ) : (
        <P2GText>
          {t('SETUP_RULES_TIME_SAVING_HOURS_MESSAGE', {
            defaultValue: 'Over the next year you could save up to {{hours}} hours*',
            hours: timeSavedInHours
          })}
        </P2GText>
      )}
    </>
  );
};

export const EstimatedTimeSavingScreen = ({ numberOfParcels, isXs, onDone, onSkip }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { track } = useAnalytics();
  const standardParcelProcessingTimeInMinute = 2;
  const [parcelsPerWeek, setParcelsPerWeek] = useState(numberOfParcels || 15);
  const [selectedRules, setSelectedRules] = useState<string[]>(possibleRules.map((r) => r.name));

  const timeSaved = useMemo(() => {
    const parcelsPerYear = parcelsPerWeek * 52;

    const percentageOfPotentialSavingAchieved = selectedRules
      .map((r) => possibleRules.find((rule) => rule.name === r)?.percentageSaved || 0)
      .reduce((sum, val) => sum + val, 0);

    const standardAnnualParcelProcessingTimeInMinutes = parcelsPerYear * standardParcelProcessingTimeInMinute;

    const savedAnnualParcelProcessingTimeInMinutes =
      (standardAnnualParcelProcessingTimeInMinutes * percentageOfPotentialSavingAchieved) / 100;

    const timeSavedInHours = Math.round(savedAnnualParcelProcessingTimeInMinutes / 60);

    return {
      parcelsPerWeek,
      percentageOfPotentialSavingAchieved,
      parcelsPerYear,
      timeSavedInHours,
      savedAnnualParcelProcessingTimeInMinutes,
      standardAnnualParcelProcessingTimeInMinutes,
      standardParcelProcessingTimeInMinute
    };
  }, [parcelsPerWeek, selectedRules]);

  return (
    <>
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            <OnboardingTitle
              text="Set defaults to save you <span>time</span>"
              translationKey="SETUP_RULES_TIME_SAVING_TITLE"
            />
            <P2GText variant="subtitle1">
              {t('SETUP_RULES_TIME_SAVING_SUBTITLE', {
                defaultValue:
                  'Some orders come in with missing shipping details. Create defaults to automatically fill the gaps and speed up your shipping process.'
              })}
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GBox sx={OnboardingScreenSpacing} textAlign="left">
          <P2GContainer maxWidth="md">
            <P2GGrid container spacing={2}>
              <P2GGrid item xs={12} sm={7}>
                <AppSurface>
                  <P2GText variant="h3" component="div">
                    {t('SETUP_RULES_TIME_SAVING_POTENTIAL_SAVINGS', {
                      defaultValue: 'Cut parcel prep time by {{percentageSaved}}%',
                      percentageSaved: SumNumber(possibleRules.map((r) => r.percentageSaved))
                    })}
                  </P2GText>
                  <P2GText component="div">
                    {t('SETUP_RULES_TIME_SAVING_SUBTITLE', {
                      defaultValue:
                        'Choose which defaults to set and see how much time you’ll save getting parcels ready to ship!'
                    })}
                  </P2GText>
                  <P2GBox display="flex" flexDirection="column" gap={2} mt={4}>
                    {possibleRules.map((rule) => (
                      <AppCard key={rule.label}>
                        <P2GBox pl={2} pr={2} pb={1} pt={1}>
                          <P2GBox
                            display="flex"
                            sx={{
                              justifyContent: { xs: undefined, sm: 'space-between' },
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' }
                            }}
                            gap={1}
                            width="100%"
                          >
                            <P2GCheckBoxLegacy
                              id={rule.label}
                              onChange={() => {
                                const alreadySelected = selectedRules.includes(rule.name);
                                if (alreadySelected) {
                                  setSelectedRules(selectedRules.filter((r) => r !== rule.name));
                                } else {
                                  setSelectedRules([...selectedRules, rule.name]);
                                }

                                track({
                                  type: alreadySelected
                                    ? 'smartsend_onboarding_rule_deselected'
                                    : 'smartsend_onboarding_rule_selected',
                                  payload: {
                                    ...GenerateBaseAnalyticsPayload([
                                      EventSections.SmartSendSetup,
                                      'estimated-time-saving-screen'
                                    ]),
                                    rule: rule.name
                                  }
                                });
                              }}
                              checked={selectedRules.includes(rule.name)}
                              label={
                                <P2GText>
                                  <Trans
                                    t={t}
                                    translationKey={rule.translationkey}
                                    components={{ strong: <strong /> }}
                                    defaults={rule.label}
                                  />
                                </P2GText>
                              }
                            />
                            <P2GChip
                              label={t('SETUP_RULES_TIME_SAVING_CHIP_LABEL', {
                                defaultValue: '{{percentageSaved}}% time saving',
                                percentageSaved: rule.percentageSaved
                              })}
                              color="info"
                              size="small"
                            />
                          </P2GBox>
                        </P2GBox>
                      </AppCard>
                    ))}
                  </P2GBox>
                </AppSurface>
              </P2GGrid>
              <P2GGrid item xs={12} sm={5}>
                <AppSurface>
                  <P2GText variant="h3" component="div">
                    {t('SETUP_RULES_ESTIMATED_TIME_SAVED', { defaultValue: 'Estimated time saved' })}
                  </P2GText>
                  <P2GText component="div">
                    <TimeSaved {...timeSaved} />
                  </P2GText>
                  <P2GBox justifySelf="center" p={2}>
                    <CircularPercentageProgress
                      value={timeSaved.percentageOfPotentialSavingAchieved}
                      size={150}
                      strokeWidth={20}
                    >
                      <P2GText variant="h1">{timeSaved.timeSavedInHours}</P2GText>
                    </CircularPercentageProgress>
                  </P2GBox>
                  <BasedOnSending parcelsPerWeek={parcelsPerWeek} setParcelsPerWeek={setParcelsPerWeek} />
                </AppSurface>
              </P2GGrid>
            </P2GGrid>
            {isXs && (
              <P2GBox mt={2}>
                <AppSurface>
                  <P2GBox textAlign="center">
                    <Messaging selectedRules={selectedRules} />
                  </P2GBox>
                </AppSurface>
              </P2GBox>
            )}
          </P2GContainer>
        </P2GBox>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        skip={
          <P2GButton variant="text" onClick={onSkip}>
            {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
          </P2GButton>
        }
        next={
          <P2GButton
            fullWidth={isXs}
            size="large"
            variant="primary"
            disabled={!selectedRules.length}
            onClick={() => onDone(selectedRules)}
            style={{ minWidth: 'unset' }}
          >
            {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
          </P2GButton>
        }
      >
        {!isXs && <Messaging selectedRules={selectedRules} />}
      </OnboardingActionBar>
    </>
  );
};
